import { ref, computed } from 'vue';
import { makeAuthenticatedApiRequest, ApiError, API_ENDPOINTS } from '../utils/apiConfig.ts';
import { useAuth } from './useAuth.js';

// Shared reactive state
const spendingLimits = ref({});
let isInitialized = false;

export function useCategorySpendingLimits() {
  const { getIdToken } = useAuth();

  // Load spending limits from API
  const loadLimits = async () => {
    try {
      const token = await getIdToken();
      const result = await makeAuthenticatedApiRequest(
        API_ENDPOINTS.SPENDING_LIMITS.GET_ALL,
        undefined,
        token
      );

      // Transform API response to match existing structure
      const limitsData = {};
      if (result.data.limits && Array.isArray(result.data.limits)) {
        result.data.limits.forEach(limitItem => {
          limitsData[limitItem.category] = {
            limit: limitItem.limit,
            createdAt: limitItem.createdAt,
            updatedAt: limitItem.updatedAt
          };
        });
      }

      spendingLimits.value = limitsData;
      isInitialized = true;
    } catch (error) {
      console.error('Error loading spending limits from API:', error);
      // Handle API errors gracefully by starting with empty limits
      spendingLimits.value = {};
      isInitialized = true;
      
      if (error instanceof ApiError && error.statusCode === 401) {
        throw new Error('Authentication required to load spending limits');
      }
      throw new Error('Failed to load spending limits from server');
    }
  };

  // Initialize if not already done
  if (!isInitialized) {
    loadLimits();
  }

  // Save spending limits to API (this method now triggers a full reload)
  const saveLimits = async () => {
    // This method is kept for backward compatibility but now triggers a reload
    await loadLimits();
  };

  // Set spending limit for a category
  const setLimit = async (category, amount) => {
    if (!category || typeof category !== 'string') {
      throw new Error('Category must be a non-empty string');
    }
    
    if (typeof amount !== 'number' || amount <= 0 || !isFinite(amount)) {
      throw new Error('Amount must be a positive number');
    }

    try {
      const normalizedCategory = category.trim().toLowerCase();
      const token = await getIdToken();
      
      const result = await makeAuthenticatedApiRequest(
        API_ENDPOINTS.SPENDING_LIMITS.SET_LIMIT,
        {
          category: normalizedCategory,
          limit: amount
        },
        token
      );

      // Update local state with the response
      if (result.data.limit) {
        spendingLimits.value[normalizedCategory] = {
          limit: result.data.limit.limit,
          createdAt: result.data.limit.createdAt,
          updatedAt: result.data.limit.updatedAt
        };
      }
      
      return true;
    } catch (error) {
      console.error('Error setting spending limit:', error);
      if (error instanceof ApiError && error.statusCode === 401) {
        throw new Error('Authentication required to set spending limits');
      }
      throw new Error('Failed to save spending limit to server');
    }
  };

  // Remove spending limit for a category
  const removeLimit = async (category) => {
    if (!category || typeof category !== 'string') {
      return false;
    }

    try {
      const normalizedCategory = category.trim().toLowerCase();
      
      if (!spendingLimits.value[normalizedCategory]) {
        return false;
      }

      const token = await getIdToken();
      
      await makeAuthenticatedApiRequest(
        API_ENDPOINTS.SPENDING_LIMITS.DELETE_CATEGORY(normalizedCategory),
        undefined,
        token
      );

      // Remove from local state
      delete spendingLimits.value[normalizedCategory];
      return true;
    } catch (error) {
      console.error('Error removing spending limit:', error);
      if (error instanceof ApiError && error.statusCode === 401) {
        throw new Error('Authentication required to remove spending limits');
      }
      if (error instanceof ApiError && error.statusCode === 404) {
        // If not found on server, remove from local state anyway
        const normalizedCategory = category.trim().toLowerCase();
        delete spendingLimits.value[normalizedCategory];
        return true;
      }
      throw new Error('Failed to remove spending limit from server');
    }
  };

  // Get spending limit for a category
  const getLimit = (category) => {
    if (!category || typeof category !== 'string') {
      return null;
    }

    const normalizedCategory = category.trim().toLowerCase();
    const limitData = spendingLimits.value[normalizedCategory];
    
    return limitData ? limitData.limit : null;
  };

  // Get full limit data (including timestamps) for a category
  const getLimitData = (category) => {
    if (!category || typeof category !== 'string') {
      return null;
    }

    const normalizedCategory = category.trim().toLowerCase();
    return spendingLimits.value[normalizedCategory] || null;
  };

  // Check if a category has a spending limit set
  const hasLimit = (category) => {
    if (!category || typeof category !== 'string') {
      return false;
    }

    const normalizedCategory = category.trim().toLowerCase();
    return normalizedCategory in spendingLimits.value;
  };

  // Get all categories that have spending limits
  const getCategoriesWithLimits = () => {
    return Object.keys(spendingLimits.value);
  };

  // Clear all spending limits
  const clearAllLimits = async () => {
    try {
      const token = await getIdToken();
      
      // Delete all limits individually (API doesn't have bulk delete)
      const categories = Object.keys(spendingLimits.value);
      const deletePromises = categories.map(category =>
        makeAuthenticatedApiRequest(
          API_ENDPOINTS.SPENDING_LIMITS.DELETE_CATEGORY(category),
          undefined,
          token
        ).catch(error => {
          // Log but don't fail the entire operation for individual deletions
          console.warn(`Failed to delete limit for category ${category}:`, error);
        })
      );
      
      await Promise.all(deletePromises);
      
      // Clear local state
      spendingLimits.value = {};
    } catch (error) {
      console.error('Error clearing all spending limits:', error);
      if (error instanceof ApiError && error.statusCode === 401) {
        throw new Error('Authentication required to clear spending limits');
      }
      throw new Error('Failed to clear spending limits from server');
    }
  };

  // Computed property for the number of categories with limits
  const limitCount = computed(() => Object.keys(spendingLimits.value).length);

  // Computed property to check if any limits are set
  const hasAnyLimits = computed(() => limitCount.value > 0);

  return {
    spendingLimits,
    limitCount,
    hasAnyLimits,
    loadLimits,
    saveLimits,
    setLimit,
    removeLimit,
    getLimit,
    getLimitData,
    hasLimit,
    getCategoriesWithLimits,
    clearAllLimits
  };
}